import React, { useState, Fragment } from 'react';
import { EditIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, PackageIcon } from 'lucide-react';
import { CatalogSchema, CatalogProduct, CatalogField } from '../CatalogsManagement';
interface ProductTableProps {
  products: CatalogProduct[];
  schema: CatalogSchema | null;
  onEdit: (product: CatalogProduct) => void;
  onToggleStatus: (productId: string) => void;
  locale: string;
}
export function ProductTable({
  products,
  schema,
  onEdit,
  onToggleStatus,
  locale
}: ProductTableProps) {
  // State for expanded rows
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Toggle row expansion
  const toggleRowExpand = (productId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };
  // Format custom field value based on its type
  const formatCustomFieldValue = (field: CatalogField, value: any) => {
    if (value === undefined || value === null) return '-';
    switch (field.dataType) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString(locale);
      case 'currency':
        return typeof value === 'object' && value.amount && value.currency ? formatCurrency(value.amount, value.currency) : value;
      default:
        return value.toString();
    }
  };
  return <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-11337">
      {products.length === 0 ? <div className="text-center py-12" data-id="element-11338">
          <PackageIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-11339" />
          <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-11340">
            No products found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-11341">
            Get started by adding a new product or adjusting your filters.
          </p>
        </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-11342">
          <thead className="bg-gray-50" data-id="element-11343">
            <tr data-id="element-11344">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11345">
                Product
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11346">
                Vendor SKU
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11347">
                Price
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11348">
                Stock
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11349">
                Manufacturer
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11350">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11351">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11352">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-11353">
            {products.map(product => <Fragment key={product.id} data-id="element-11354">
                <tr className={expandedRows[product.id] ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-11355">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-11356">
                    <div className="flex items-center" data-id="element-11357">
                      <button onClick={() => toggleRowExpand(product.id)} className="mr-2 text-gray-400 hover:text-gray-500" title={expandedRows[product.id] ? 'Collapse' : 'Expand'} data-id="element-11358">
                        {expandedRows[product.id] ? <ChevronUpIcon className="h-4 w-4" data-id="element-11359" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-11360" />}
                      </button>
                      <div className="text-sm font-medium text-indigo-600" data-id="element-11361">
                        {product.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11362">
                    {product.vendorSku}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11363">
                    {formatCurrency(product.price, product.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11364">
                    {product.availableStock > 0 ? <span className="text-green-600" data-id="element-11365">
                        {product.availableStock}
                      </span> : <span className="text-red-600" data-id="element-11366">Out of stock</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11367">
                    {product.manufacturer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11368">
                    {product.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-11369">
                    {product.isActive ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-11370">
                        <CheckIcon className="h-4 w-4 mr-1" data-id="element-11371" />
                        Active
                      </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-11372">
                        <XIcon className="h-4 w-4 mr-1" data-id="element-11373" />
                        Inactive
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11374">
                    <div className="flex justify-end space-x-2" data-id="element-11375">
                      <button onClick={() => onEdit(product)} className="text-indigo-600 hover:text-indigo-900" title="Edit Product" data-id="element-11376">
                        <EditIcon className="h-4 w-4" data-id="element-11377" />
                      </button>
                      <button onClick={() => onToggleStatus(product.id)} className={product.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} title={product.isActive ? 'Deactivate' : 'Activate'} data-id="element-11378">
                        {product.isActive ? <XIcon className="h-4 w-4" data-id="element-11379" /> : <CheckIcon className="h-4 w-4" data-id="element-11380" />}
                      </button>
                    </div>
                  </td>
                </tr>
                {/* Expanded row with custom attributes and additional details */}
                {expandedRows[product.id] && <tr data-id="element-11381">
                    <td colSpan={8} className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-11382">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-11383">
                        <div data-id="element-11384">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-11385">
                            Product Details
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-11386">
                            {product.hsCode && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-11387">
                                  HS Code
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-11388">
                                  {product.hsCode}
                                </dd>
                              </>}
                            {product.warranty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-11389">
                                  Warranty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-11390">
                                  {product.warranty}
                                </dd>
                              </>}
                            <dt className="text-sm font-medium text-gray-500" data-id="element-11391">
                              Active From
                            </dt>
                            <dd className="text-sm text-gray-900" data-id="element-11392">
                              {new Date(product.activeFrom).toLocaleDateString(locale)}
                            </dd>
                            {product.activeTo && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-11393">
                                  Active To
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-11394">
                                  {new Date(product.activeTo).toLocaleDateString(locale)}
                                </dd>
                              </>}
                          </dl>
                        </div>
                        <div data-id="element-11395">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-11396">
                            Packaging
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-11397">
                            {product.outerPackQty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-11398">
                                  Outer Pack Qty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-11399">
                                  {product.outerPackQty} {product.outerPackUnit}
                                </dd>
                              </>}
                            {product.innerPackQty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-11400">
                                  Inner Pack Qty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-11401">
                                  {product.innerPackQty} {product.innerPackUnit}
                                </dd>
                              </>}
                          </dl>
                        </div>
                        <div data-id="element-11402">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-11403">
                            Inventory
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-11404">
                            <dt className="text-sm font-medium text-gray-500" data-id="element-11405">
                              Available
                            </dt>
                            <dd className="text-sm text-gray-900" data-id="element-11406">
                              {product.availableStock}
                            </dd>
                            {product.backordered !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-11407">
                                  Backordered
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-11408">
                                  {product.backordered}
                                </dd>
                              </>}
                            {product.qtyOrdered !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-11409">
                                  Ordered
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-11410">
                                  {product.qtyOrdered}
                                </dd>
                              </>}
                            {product.qtyInvoiced !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-11411">
                                  Invoiced
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-11412">
                                  {product.qtyInvoiced}
                                </dd>
                              </>}
                          </dl>
                        </div>
                      </div>
                      {/* Custom attributes */}
                      {schema && schema.fields.length > 0 && Object.keys(product.customAttributes).length > 0 && <div className="mt-4" data-id="element-11413">
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-11414">
                              Custom Attributes
                            </h4>
                            <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2" data-id="element-11415">
                              {schema.fields.map(field => <Fragment key={field.id} data-id="element-11416">
                                  <dt className="text-sm font-medium text-gray-500" data-id="element-11417">
                                    {field.name}
                                  </dt>
                                  <dd className="text-sm text-gray-900" data-id="element-11418">
                                    {formatCustomFieldValue(field, product.customAttributes[field.key])}
                                  </dd>
                                </Fragment>)}
                            </dl>
                          </div>}
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>}
    </div>;
}